import { APIStatus } from '@application/enums/api-status';
import { createSlice } from '@reduxjs/toolkit';
import { createUserAsync, getAllUsersPaginatedAsync, getUserWithRolesByIdAsync, toggleUserActiveAsync, updateUserAsync } from './userThunk';
import { getAllUsersWithoutPagination, getAllUsersByCompany } from './userThunk';
const initialState = {
    users: null,
    selectedUserWithRoles: null,
    status: APIStatus.IDLE,
    usersWithoutPagination: [],
    usersByCompany: [],
    companiesFetched: [],
    selectedUser: null,
    searchString: null
};
export const UserSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setSelectedUser: (state, action) => {
            state.selectedUser = action.payload;
        },
        clearSelectedUser: (state) => {
            state.selectedUser = null;
        },
        clearSelectedUserWithRoles: (state) => {
            state.selectedUserWithRoles = null;
        },
        clearUsersByCompany: (state) => {
            state.usersByCompany = [];
            state.companiesFetched = [];
        },
        setSearchString: (state, action) => {
            state.searchString = action.payload;
        },
        resetSearchString: (state) => {
            state.searchString = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAllUsersWithoutPagination.pending, (state) => {
            state.status = APIStatus.PENDING;
        })
            .addCase(getAllUsersWithoutPagination.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.usersWithoutPagination = action.payload;
        })
            .addCase(getAllUsersWithoutPagination.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = { error: action.payload || 'Failed to fetch users', status: 500 };
        })
            .addCase(getAllUsersByCompany.pending, (state) => {
            state.status = APIStatus.PENDING;
        })
            .addCase(getAllUsersByCompany.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.usersByCompany = action.payload;
            // Store the company IDs that were fetched
            if (action.meta.arg) {
                state.companiesFetched = action.meta.arg;
            }
        })
            .addCase(getAllUsersByCompany.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = {
                error: action.payload,
                status: 500
            };
        })
            .addCase(getAllUsersPaginatedAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getAllUsersPaginatedAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.users = action.payload;
        })
            .addCase(getAllUsersPaginatedAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        })
            .addCase(createUserAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(createUserAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            if (state.users && Array.isArray(state.users.items) && action.payload.data) {
                state.users.items.push(action.payload.data);
                state.users.totalCount += 1;
            }
        })
            .addCase(createUserAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        })
            .addCase(getUserWithRolesByIdAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getUserWithRolesByIdAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.selectedUserWithRoles = action.payload;
        })
            .addCase(getUserWithRolesByIdAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        })
            .addCase(updateUserAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(updateUserAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            if (state.users && Array.isArray(state.users.items) && action.payload.data) {
                const data = action.payload.data;
                const index = state.users.items.findIndex((user) => user.id === data.id);
                if (index !== -1) {
                    state.users.items[index] = data;
                }
                state.selectedUser = data;
            }
        })
            .addCase(updateUserAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        })
            .addCase(toggleUserActiveAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(toggleUserActiveAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            // Update the active status in the roles list
            if (state.users && Array.isArray(state.users.items)) {
                const index = state.users.items.findIndex((user) => user.id === action.payload.data?.id);
                if (index !== -1) {
                    state.users.items[index].isActive = action.payload?.data?.isActive ?? false;
                }
            }
            // Optionally update selectedRole if it matches
            if (state.selectedUser && state.selectedUser?.id === action.payload.data?.id) {
                state.selectedUser.isActive = action.payload?.data?.isActive ?? false;
            }
            if (state.selectedUserWithRoles &&
                state.selectedUserWithRoles?.id === action.payload.data?.id) {
                state.selectedUserWithRoles.isActive = action.payload?.data?.isActive ?? false;
            }
        })
            .addCase(toggleUserActiveAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
    }
});
export const { setSelectedUser, clearSelectedUser, clearSelectedUserWithRoles, clearUsersByCompany, setSearchString, resetSearchString } = UserSlice.actions;
export default UserSlice.reducer;
